import React, { useState } from 'react';
import { PrismAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus as HighlightTheme } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { tomorrow as HighlightThemeVsc } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { GrLinkPrevious, GrLinkNext } from 'react-icons/gr';
import {useAppStore} from "../../../../../../../GlobalStore/GlobalStore.ts";
import {isIde, sendMessageToIde} from "../../../../../../../Utils/misc/envNameUtils.ts";

interface CodeBlockProps {
    language: string;
    children: string;
    index: number;
    node?: any;
}

const CodeBlock: React.FC<CodeBlockProps> = ({ language, children, index, node }) => {
    const codeblockId = `${index}-${node?.position?.start.line || 0}`;
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment, vscode } = environmentObj;
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { applyingCodeBlockId } = dynamicStateObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const [copyText, setCopyText] = useState<{ [key: string]: string }>({});

    const copyToClipboard = (text: string, buttonId: string) => {
        navigator.clipboard.writeText(text.trimEnd()).then(
            () => {
                setCopyText((prevState) => ({
                    ...prevState,
                    [buttonId]: 'Copied!',
                }));
                setTimeout(() => {
                    setCopyText((prevState) => ({
                        ...prevState,
                        [buttonId]: 'Copy',
                    }));
                }, 2000);
            },
            (err) => {
                console.error('Failed to copy text: ', err);
            }
        );
    };

    const handleAddToFileClick = () => {
        setDynamicStateObj({applyingCodeBlockId: codeblockId});

        sendMessageToIde(environment, vscode, "getCurrentFileCode", [{ codeBlock: String(children) }]);
    }

    return (
        <div className="rounded-lg overflow-hidden my-5 -mx-2">
            <div className="flex justify-between items-center px-4 pb-1 pt-2 bg-gray-800 mx-auto rounded-t-2xl min-w-60">
                <span className="text-xs text-white font-sans">{language}</span>
                <div className="flex gap-2">
                    {isIde(environment) && (
                        applyingCodeBlockId === codeblockId ? (
                            <div
                                className="px-2 pt-0.5 cursor-pointer"
                                onClick={() => setDynamicStateObj({applyingCodeBlockId: null})}
                            >
                                <div className="flex items-center justify-center w-5 h-5">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-100 border-t-transparent"></div>
                                </div>
                            </div>
                        ) : (
                            <button
                                className="text-xs text-white font-sans px-2.5 py-0.5 rounded-xl flex items-center"
                                onClick={handleAddToFileClick}
                            >
                                {environment === "vscode" ? <GrLinkNext className="text-lg" /> : <GrLinkPrevious className="text-lg" />}
                            </button>
                        )
                    )}
                    <button
                        className="text-xs text-white font-sans px-2.5 py-0.5 border border-neutral-400 rounded-xl hover:bg-gray-700 hover:bg-opacity-70"
                        onClick={() =>
                            copyToClipboard(
                                String(children),
                                codeblockId
                            )
                        }
                    >
                        {copyText[codeblockId] || 'Copy'}
                    </button>
                </div>
            </div>
            <div className="rounded-b-2xl overflow-hidden">
                <SyntaxHighlighter
                    style={environment === "vscode" ? HighlightThemeVsc : HighlightTheme}
                    language={language}
                    PreTag="div"
                    className={`!m-0 !bg-black ${environment === "vscode" ? "!text-xs" : ""}`}
                >
                    {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
            </div>
        </div>
    );
};

export default CodeBlock;